import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Building2, Boxes, Layers, Truck, MapPin, Package, User, Trash2, AlertTriangle, Edit, Car } from 'lucide-react';
import { useCompany } from '../contexts/CompanyContext';
import { MultiSelect } from '../components/ui/multi-select';

const DeleteConfirmDialog = ({ open, onOpenChange, title, description, onConfirm, loading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
          {title}
        </DialogTitle>
        <DialogDescription className="text-slate-600 pt-2">{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700">
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const UNIT_OPTIONS = ['Meters', 'Bales', 'Thans', 'Kg', 'Pieces'];

const Masters = () => {
  const { activeCompany } = useCompany();
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [transports, setTransports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAllMasters(); }, [activeCompany?.company_id]);

  const fetchAllMasters = async () => {
    try {
      const [s, q, sq, t, l, i, d] = await Promise.all([
        api.get('/suppliers'), api.get('/qualities'), api.get('/subqualities'),
        api.get('/transports'), api.get('/locations'), api.get('/items'),
        api.get('/drivers')
      ]);
      setSuppliers(s.data); setQualities(q.data); setSubqualities(sq.data);
      setTransports(t.data); setLocations(l.data); setItems(i.data);
      setDrivers(d.data); setLoading(false);
    } catch { toast.error('Failed to load masters data'); setLoading(false); }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
    </div>
  );

  return (
    <div data-testid="masters-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Masters Management</h1>
        <p className="text-slate-600 mt-1">Manage suppliers, qualities, items, locations, drivers and transports</p>
      </div>
      <Tabs defaultValue="suppliers" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-1">
          <TabsTrigger value="suppliers" data-testid="suppliers-tab" className="text-xs"><Building2 className="h-3 w-3 mr-1" />Suppliers ({suppliers.length})</TabsTrigger>
          <TabsTrigger value="qualities" data-testid="qualities-tab" className="text-xs"><Boxes className="h-3 w-3 mr-1" />Qualities ({qualities.length})</TabsTrigger>
          <TabsTrigger value="subqualities" data-testid="finish-qualities-tab" className="text-xs"><Layers className="h-3 w-3 mr-1" />Finish ({subqualities.length})</TabsTrigger>
          <TabsTrigger value="items" data-testid="items-tab" className="text-xs"><Package className="h-3 w-3 mr-1" />Items ({items.length})</TabsTrigger>
          <TabsTrigger value="locations" data-testid="locations-tab" className="text-xs"><MapPin className="h-3 w-3 mr-1" />Locations ({locations.length})</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="drivers-tab" className="text-xs"><User className="h-3 w-3 mr-1" />Drivers ({drivers.length})</TabsTrigger>
          <TabsTrigger value="transports" data-testid="transports-tab" className="text-xs"><Truck className="h-3 w-3 mr-1" />Transports ({transports.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="suppliers"><SuppliersList suppliers={suppliers} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="qualities"><QualitiesList qualities={qualities} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="subqualities"><SubQualitiesList subqualities={subqualities} qualities={qualities} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="items"><ItemsList items={items} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="locations"><LocationsList locations={locations} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="drivers"><DriversList drivers={drivers} onUpdate={fetchAllMasters} /></TabsContent>
        <TabsContent value="transports"><TransportsList transports={transports} onUpdate={fetchAllMasters} /></TabsContent>
      </Tabs>
    </div>
  );
};

// ============= SUPPLIERS =============
const SupplierForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData?.name || '', city: initialData?.city || '',
    contact_person: initialData?.contact_person || '', phone: initialData?.phone || '',
    email: initialData?.email || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/suppliers/${initialData.supplier_id}`, formData); toast.success('Supplier updated!'); }
      else { await api.post('/suppliers', formData); toast.success('Supplier added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save supplier'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Supplier Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
      <div><Label>City *</Label><Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required /></div>
      <div><Label>Contact Person</Label><Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} /></div>
      <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
      <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Supplier'}</Button>
    </form>
  );
};

const SuppliersList = ({ suppliers, onUpdate }) => {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = suppliers.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.city?.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/suppliers/${deleteTarget.supplier_id}`); toast.success('Supplier deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete supplier'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <CardTitle>Suppliers</CardTitle>
            <div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search suppliers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/bulk/suppliers')} data-testid="bulk-add-suppliers-btn" className="gap-1.5"><Plus className="h-4 w-4" />Bulk Add</Button>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Supplier</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Add New Supplier</DialogTitle></DialogHeader><SupplierForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
          </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((supplier) => (
            <div key={supplier.supplier_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-900">{supplier.name}</span>
                    <Badge variant="outline" className="text-xs">{supplier.supplier_code}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <div>City: {supplier.city}</div>
                    {supplier.contact_person && <div>Contact: {supplier.contact_person}</div>}
                    {supplier.phone && <div>Phone: {supplier.phone}</div>}
                    {supplier.email && <div>Email: {supplier.email}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(supplier)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(supplier)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Supplier</DialogTitle></DialogHeader>
          {editTarget && <SupplierForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Supplier" description={`Are you sure you want to delete "${deleteTarget?.name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= QUALITIES =============
const QualityForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    quality_name: initialData?.quality_name || '', default_unit: initialData?.default_unit || '',
    unit_type: initialData?.unit_type || '', description: initialData?.description || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/qualities/${initialData.quality_id}`, formData); toast.success('Quality updated!'); }
      else { await api.post('/qualities', formData); toast.success('Quality added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save quality'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Quality Name *</Label><Input value={formData.quality_name} onChange={(e) => setFormData({ ...formData, quality_name: e.target.value })} placeholder="Cotton 100%" required /></div>
      <div><Label>Default Unit *</Label>
        <Select value={formData.default_unit} onValueChange={(value) => setFormData({ ...formData, default_unit: value, unit_type: value })}>
          <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
          <SelectContent>{UNIT_OPTIONS.map((unit) => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Quality'}</Button>
    </form>
  );
};

const QualitiesList = ({ qualities, onUpdate }) => {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = qualities.filter(q => q.quality_name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/qualities/${deleteTarget.quality_id}`); toast.success('Quality deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete quality'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Qualities</CardTitle><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search qualities..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/bulk/qualities')} data-testid="bulk-add-qualities-btn" className="gap-1.5"><Plus className="h-4 w-4" />Bulk Add</Button>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Quality</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Add New Quality</DialogTitle></DialogHeader><QualityForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((quality) => (
            <div key={quality.quality_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-900">{quality.quality_name}</span>
                    <Badge variant="outline" className="text-xs">{quality.quality_code}</Badge>
                    <Badge className="bg-teal-50 text-teal-700 border-teal-200 text-xs">{quality.default_unit}</Badge>
                  </div>
                  {quality.description && <p className="text-sm text-slate-600">{quality.description}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(quality)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(quality)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Quality</DialogTitle></DialogHeader>
          {editTarget && <QualityForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Quality" description={`Are you sure you want to delete "${deleteTarget?.quality_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= FINISH QUALITIES =============
const SubQualityForm = ({ qualities, onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subquality_name: initialData?.subquality_name || '',
    quality_ids: initialData?.quality_ids?.length
      ? initialData.quality_ids
      : (initialData?.quality_id ? [initialData.quality_id] : []),
    description: initialData?.description || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quality_ids || formData.quality_ids.length === 0) {
      toast.error('Please select at least one parent quality');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        subquality_name: formData.subquality_name,
        quality_ids: formData.quality_ids,
        description: formData.description,
      };
      if (mode === 'edit') { await api.put(`/subqualities/${initialData.subquality_id}`, payload); toast.success('Finish Quality updated!'); }
      else { await api.post('/subqualities', payload); toast.success('Finish Quality added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save finish quality'); setLoading(false); }
  };
  const qualityOptions = qualities.map(q => ({ value: q.quality_id, label: q.quality_name }));
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Parent Qualities * <span className="text-xs text-slate-500 font-normal">(select one or more)</span></Label>
        <MultiSelect
          value={formData.quality_ids}
          onChange={(next) => setFormData({ ...formData, quality_ids: next })}
          options={qualityOptions}
          placeholder="Select parent qualities"
          testId="subquality-parent-multiselect"
        />
      </div>
      <div><Label>Finish Quality Name *</Label><Input value={formData.subquality_name} onChange={(e) => setFormData({ ...formData, subquality_name: e.target.value })} placeholder="40s Combed" required /></div>
      <div><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Finish Quality'}</Button>
    </form>
  );
};

const SubQualitiesList = ({ subqualities, qualities, onUpdate }) => {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = subqualities.filter(sq => sq.subquality_name.toLowerCase().includes(searchTerm.toLowerCase()));
  const getQualityName = (qualityId) => qualities.find(q => q.quality_id === qualityId)?.quality_name || 'Unknown';
  const getParentNames = (sq) => {
    const ids = (sq.quality_ids && sq.quality_ids.length) ? sq.quality_ids : (sq.quality_id ? [sq.quality_id] : []);
    return ids.map(getQualityName);
  };
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/subqualities/${deleteTarget.subquality_id}`); toast.success('Finish Quality deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete finish quality'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Finish Qualities</CardTitle><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search finish qualities..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/bulk/finish-qualities')} data-testid="bulk-add-finish-qualities-btn" className="gap-1.5"><Plus className="h-4 w-4" />Bulk Add</Button>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Finish Quality</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Add New Finish Quality</DialogTitle></DialogHeader><SubQualityForm qualities={qualities} onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((sq) => (
            <div key={sq.subquality_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-900">{sq.subquality_name}</span>
                    <Badge variant="outline" className="text-xs">{sq.subquality_code}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-slate-500">Parent{getParentNames(sq).length > 1 ? 's' : ''}:</span>
                    {getParentNames(sq).map((name, i) => (
                      <Badge key={i} className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-normal">{name}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(sq)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(sq)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Finish Quality</DialogTitle></DialogHeader>
          {editTarget && <SubQualityForm qualities={qualities} initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Finish Quality" description={`Are you sure you want to delete "${deleteTarget?.subquality_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= TRANSPORTS =============
const TransportForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    transport_name: initialData?.transport_name || '', contact_person: initialData?.contact_person || '',
    phone: initialData?.phone || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/transports/${initialData.transport_id}`, formData); toast.success('Transport updated!'); }
      else { await api.post('/transports', formData); toast.success('Transport added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save transport'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Transport Name *</Label><Input value={formData.transport_name} onChange={(e) => setFormData({ ...formData, transport_name: e.target.value })} placeholder="VRL Logistics" required /></div>
      <div><Label>Contact Person</Label><Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} /></div>
      <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Transport'}</Button>
    </form>
  );
};

const TransportsList = ({ transports, onUpdate }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = transports.filter(t => t.transport_name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/transports/${deleteTarget.transport_id}`); toast.success('Transport deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete transport'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Transports</CardTitle><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search transports..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Transport</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Add New Transport</DialogTitle></DialogHeader><TransportForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((t) => (
            <div key={t.transport_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-900">{t.transport_name}</span>
                    <Badge variant="outline" className="text-xs">{t.transport_code}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    {t.contact_person && <div>Contact: {t.contact_person}</div>}
                    {t.phone && <div>Phone: {t.phone}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(t)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(t)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Transport</DialogTitle></DialogHeader>
          {editTarget && <TransportForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Transport" description={`Are you sure you want to delete "${deleteTarget?.transport_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= ITEMS =============
const ItemForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ item_name: initialData?.item_name || '', description: initialData?.description || '' });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/items/${initialData.item_id}`, formData); toast.success('Item updated!'); }
      else { await api.post('/items', formData); toast.success('Item added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save item'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Item Name *</Label><Input value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} placeholder="Grey Fabric" required /></div>
      <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Item description..." /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Item'}</Button>
    </form>
  );
};

const ItemsList = ({ items, onUpdate }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = items.filter(i => i.item_name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/items/${deleteTarget.item_id}`); toast.success('Item deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete item'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Items</CardTitle><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search items..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Item</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Add New Item</DialogTitle></DialogHeader><ItemForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((item) => (
            <div key={item.item_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-slate-900">{item.item_name}</span>
                    <Badge variant="outline" className="text-xs">{item.item_code}</Badge>
                  </div>
                  {item.description && <p className="text-sm text-slate-600">{item.description}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(item)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(item)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-slate-500 text-center py-4">No items found</p>}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Item</DialogTitle></DialogHeader>
          {editTarget && <ItemForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Item" description={`Are you sure you want to delete "${deleteTarget?.item_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= LOCATIONS =============
const LocationForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    location_name: initialData?.location_name || '', address: initialData?.address || '',
    description: initialData?.description || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/locations/${initialData.location_id}`, formData); toast.success('Location updated!'); }
      else { await api.post('/locations', formData); toast.success('Location added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save location'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Location Name *</Label><Input value={formData.location_name} onChange={(e) => setFormData({ ...formData, location_name: e.target.value })} placeholder="Jasol Unit" required /></div>
      <div><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
      <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Location'}</Button>
    </form>
  );
};

const LocationsList = ({ locations, onUpdate }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = locations.filter(l => l.location_name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/locations/${deleteTarget.location_id}`); toast.success('Location deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete location'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Locations</CardTitle><p className="text-sm text-slate-500 mt-1">Factory/warehouse locations for Grey Receipts</p><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search locations..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Location</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Add New Location</DialogTitle></DialogHeader><LocationForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((loc) => (
            <div key={loc.location_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-slate-900">{loc.location_name}</span>
                    <Badge variant="outline" className="text-xs">{loc.location_code}</Badge>
                  </div>
                  <div className="text-sm text-slate-600">
                    {loc.address && <div>Address: {loc.address}</div>}
                    {loc.description && <div>{loc.description}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(loc)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(loc)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-slate-500 text-center py-4">No locations found</p>}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Location</DialogTitle></DialogHeader>
          {editTarget && <LocationForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Location" description={`Are you sure you want to delete "${deleteTarget?.location_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

// ============= DRIVERS =============
const DriverForm = ({ onSuccess, initialData, mode = 'create' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    driver_name: initialData?.driver_name || '',
    mobile_number: initialData?.mobile_number || '',
    alternate_mobile: initialData?.alternate_mobile || '',
    vehicle_number: initialData?.vehicle_number || '',
    notes: initialData?.notes || ''
  });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === 'edit') { await api.put(`/drivers/${initialData.driver_id}`, formData); toast.success('Driver updated!'); }
      else { await api.post('/drivers', formData); toast.success('Driver added!'); }
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save driver'); setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><Label>Driver Name *</Label><Input value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} placeholder="Full name" required /></div>
        <div><Label>Mobile Number *</Label><Input value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })} placeholder="+91 98765 43210" required /></div>
        <div><Label>Alternate Mobile</Label><Input value={formData.alternate_mobile} onChange={(e) => setFormData({ ...formData, alternate_mobile: e.target.value })} /></div>
        <div className="col-span-2"><Label>Vehicle Number</Label><Input value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })} placeholder="MH12AB1234" /></div>
        <div className="col-span-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." /></div>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Driver'}</Button>
    </form>
  );
};

const DriversList = ({ drivers, onUpdate }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const filtered = drivers.filter(d => d.driver_name.toLowerCase().includes(searchTerm.toLowerCase()) || d.mobile_number?.includes(searchTerm));
  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try { await api.delete(`/drivers/${deleteTarget.driver_id}`); toast.success('Driver deleted'); setDeleteTarget(null); onUpdate(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete driver'); }
    finally { setDeleteLoading(false); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1"><CardTitle>Lorry Drivers</CardTitle><p className="text-sm text-slate-500 mt-1">Driver details for LR delivery issuing</p><div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input placeholder="Search drivers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" />Add Driver</Button></DialogTrigger>
            <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add New Driver</DialogTitle></DialogHeader><DriverForm onSuccess={() => { setShowAdd(false); onUpdate(); }} /></DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((driver) => (
            <div key={driver.driver_id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-slate-900">{driver.driver_name}</span>
                    <Badge variant="outline" className="text-xs">{driver.driver_code}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <div>Mobile: {driver.mobile_number}</div>
                    {driver.alternate_mobile && <div>Alt Mobile: {driver.alternate_mobile}</div>}
                    {driver.vehicle_number && <div className="flex items-center gap-1"><Car className="h-3 w-3" />Vehicle: {driver.vehicle_number}</div>}
                  </div>
                  {driver.notes && <p className="text-sm text-slate-500 mt-2 italic">{driver.notes}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => setEditTarget(driver)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(driver)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-slate-500 text-center py-4">No drivers found</p>}
        </div>
      </CardContent>
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Edit Driver</DialogTitle></DialogHeader>
          {editTarget && <DriverForm initialData={editTarget} mode="edit" onSuccess={() => { setEditTarget(null); onUpdate(); }} />}
        </DialogContent>
      </Dialog>
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} title="Delete Driver" description={`Are you sure you want to delete "${deleteTarget?.driver_name}"?`} onConfirm={handleDelete} loading={deleteLoading} />
    </Card>
  );
};

export default Masters;
